import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { ProductResultsContext } from './ProductResultsContext';
import { StandardDimension } from './Dimensions/StandardDimension';
import { LinkDimension } from './Dimensions/LinkDimension';
import { RatingStarDimension } from './Dimensions/RatingStarDimension';
import { WantMoreDimension } from './Dimensions/WantMoreDimension';
import { DimensionsPlaceholder } from './Dimensions/DimensionsPlaceholder';
import { VisualDimension } from './Dimensions/VisualDimension';
import { NumericRangeDimension } from './Dimensions/NumericRangeDimension';
import {
  sortDimensions, getRefinementUrlWithSort, showMoreFilters, getRefinementIndex
} from '../product-results-helpers';

import '../styles/results-dimensions.scss';

const ResultsDimensions = ({
  onDimensionsChange,
  enableMultiStore,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  onDisplay,
  onDisplayChange,
  shiftCategoryDimension
}) => {
  const { data, loading } = useContext(ProductResultsContext);

  const { taxonomy, dimensions, searchReport, metadata } = data?.searchModel || {};
  if (!dimensions) {
    return <DimensionsPlaceholder numOf={5} />;
  }
  if (dimensions.length === 0) return null;

  const { breadCrumbs = [] } = taxonomy || {};

  const sortedDimensions = sortDimensions({ dimensions, searchReport, metadata, shiftCategoryDimension });

  const onRefinementChange = ({ refinement, dimension, onDisplay: displayValue }) => {
    if (onDimensionsChange) {
      const refinementUrl = getRefinementUrlWithSort({ dimension, metadata, refinement, searchReport });
      const { label: dimensionName } = dimension;
      const updatedRefinement = {
        ...refinement,
        url: refinementUrl,
        redirectUrl: dimensionName.toUpperCase() === 'CATEGORY' ? refinementUrl : null,
        dimensionName,
        refinementIndex: getRefinementIndex(refinement, dimension)
      };
      onDimensionsChange({ refinement: updatedRefinement, onDisplay: displayValue });
    }
  };

  const dimensionsClass = classNames('results-dimensions', {
    'results-dimensions--fade': loading
  });

  const onDisplayData = {
    onDisplay,
    onDisplayChange,
    isStoreDisplay: data?.searchModel?.metadata?.isStoreDisplay || null,
    itemCount: data?.searchModel?.metadata?.productCount?.inStore
  };

  return (
    <div className={dimensionsClass}>
      {sortedDimensions.map((dimension) => {
        const {
          dimensionId, label, collapse, isVisualDimension, isNumericFilter,
        } = dimension;
        const hasFilter = ['BRAND', 'COLOR FAMILY', 'PATTERN TYPE', 'THEME', 'FEATURED KEYWORDS',
          'CUSTOMER SEARCHED KEYWORDS', 'POPULAR KEYWORDS'].indexOf(label.toUpperCase()) > -1;

        if (label.toUpperCase() === 'CATEGORY') {
          return (
            <LinkDimension
              collapsed={collapse}
              key={dimensionId}
              dimension={dimension}
              breadCrumbs={breadCrumbs}
              onChange={onRefinementChange}
            />
          );
        }
        if (label.toUpperCase() === 'REVIEW RATING') {
          return (
            <RatingStarDimension
              collapsed={collapse}
              key={dimensionId}
              dimension={dimension}
              onChange={onRefinementChange}
            />
          );
        }
        if (isVisualDimension) {
          return (
            <VisualDimension
              key={dimensionId}
              collapsed={collapse}
              onChange={onRefinementChange}
              dimension={dimension}
            />
          );
        }
        if (isNumericFilter) {
          return (
            <NumericRangeDimension
              collapsed={collapse}
              dimension={dimension}
              enableMultiStore={enableMultiStore}
              filter
              key={dimensionId}
              metadata={metadata}
              multiStoreSelection={multiStoreSelection}
              onChange={onRefinementChange}
              onDisplayData={onDisplayData}
              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
            />
          );
        }
        if (hasFilter) {
          return (
            <StandardDimension
              collapsed={collapse}
              filter
              key={dimensionId}
              dimension={dimension}
              onChange={onRefinementChange}
              enableMultiStore={enableMultiStore}
              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
              multiStoreSelection={multiStoreSelection}
              onDisplayData={onDisplayData}
            />
          );
        }
        return (
          <StandardDimension
            collapsed={collapse}
            priceRange={label.toUpperCase() === 'PRICE'}
            key={dimensionId}
            dimension={dimension}
            onChange={onRefinementChange}
            enableMultiStore={enableMultiStore}
            onMultiStoreSelectionChange={onMultiStoreSelectionChange}
            multiStoreSelection={multiStoreSelection}
            onDisplayData={onDisplayData}
            isNumericFilter={isNumericFilter}
            metadata={metadata}
          />
        );
      })}
      {showMoreFilters({ dimensions, breadCrumbs }) && (
        <WantMoreDimension />
      )}
    </div>
  );
};

ResultsDimensions.propTypes = {
  onDimensionsChange: PropTypes.func.isRequired,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onMultiStoreSelectionChange: PropTypes.func,
  onDisplay: PropTypes.string,
  onDisplayChange: PropTypes.func,
  shiftCategoryDimension: PropTypes.bool
};

ResultsDimensions.defaultProps = {
  enableMultiStore: false,
  onMultiStoreSelectionChange: null,
  multiStoreSelection: null,
  onDisplay: 'false',
  onDisplayChange: null,
  shiftCategoryDimension: false
};

ResultsDimensions.displayName = 'Dimensions';

ResultsDimensions.dataModel = extend(StandardDimension);

export { ResultsDimensions };
